/** /notification_test_render/ — side-effect-free full-composition render of a
 *  notification's delivery methods with per-channel composition provenance.
 *
 * Renders through the real delivery seams (block namespaces, CUSTOM/STANDARD
 * templates, theme shell wrap + signatures, language policy) without sending
 * or writing anything. Gated by `console_notification_blocks_view`.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules).
 */
import { apiGet, apiPost } from './client';
import type { EvaluateContext, OverrideChannel, SupportedEvent } from './notificationBlocks';

/** Channels the test render supports — the composition-aware delivery methods. */
export const RENDER_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'chat', label: 'Chat' },
  { value: 'airbnb', label: 'Airbnb' },
] as const;
export type RenderChannel = (typeof RENDER_CHANNELS)[number]['value'];

export interface TestRenderRequest {
  notification: number;
  /** Narrow to one delivery method (allowed even when disabled, so drafts can
   *  be tested). Default: all enabled methods on renderable channels. */
  delivery_method?: number;
  /** Filter the rendered methods by channel. */
  channels?: RenderChannel[];
  /** Event code override; defaults to the notification's own event. */
  event?: string;
  context: EvaluateContext;
  /** Wrap each string block value in `<!-- block:<codename> v<pk> -->` markers. */
  debug_markers?: boolean;
  /** Draft edits applied to the IN-MEMORY delivery method before rendering
   *  (never saved). Requires `delivery_method` + `console_notifications_manage`.
   *  Keys: per-channel template sources, `*_rendering_type`, `*_template` (id),
   *  `theme` (id). */
  field_overrides?: Record<string, unknown>;
  /** Draft notification-level edits (never saved); same permission. Keys:
   *  `disable_default_blocks`, `language_policy` (id). */
  notification_overrides?: Record<string, unknown>;
}

/** RENDER_STANDARD: {id, name, version, template_language}; CUSTOM: {custom: true}. */
export interface CompositionLayout {
  custom?: boolean;
  id?: number;
  name?: string;
  version?: number;
  template_language?: 'jinja2' | 'django';
}

export interface CompositionTheme {
  id: number;
  codename: string;
  tour_operator_id: number | null;
  inline_css: boolean;
  updated_ts: string | null;
  /** Email only — the body was wrapped in the theme's email shell. */
  shell_wrapped?: boolean;
  /** Text channels only — the theme's per-channel signature was appended. */
  signature_appended?: boolean;
}

export interface CompositionBlockEntry {
  id: number;
  alias: string;
  codename: string;
  type: string;
  updated_ts: string | null;
  format: 'html' | 'markdown' | 'plaintext';
  channel_override_used: boolean;
  /** base: the canonical (no-channel, html) evaluation; channel: a per-channel re-evaluation. */
  source: 'base' | 'channel';
}

/** The same record real deliveries archive as composition.json in the S3 ZIP. */
export interface CompositionRecord {
  delivery_method_id: number;
  layout: CompositionLayout;
  theme: CompositionTheme | null;
  blocks: CompositionBlockEntry[];
  language: { policy_id: number | null; resolved: string | null };
}

export interface TestRenderChannelResult {
  delivery_method: number;
  channel: RenderChannel;
  /** Rendered subject — email only. */
  subject: string | null;
  /** Rendered body after theme shell wrap / signature; null when error is set. */
  body: string | null;
  /** How the body should be displayed (email html, whatsapp markdown, rest plaintext). */
  format: 'html' | 'markdown' | 'plaintext';
  composition: CompositionRecord | null;
  /** Render failure or delivery-would-abort reason for this channel. */
  error: string | null;
}

export interface TestRenderResult {
  resolved: boolean;
  language: string | null;
  results: TestRenderChannelResult[];
}

export function testRender(body: TestRenderRequest): Promise<TestRenderResult> {
  return apiPost<TestRenderResult>('/notification_test_render/', body);
}

export function listTestRenderEvents(): Promise<SupportedEvent[]> {
  return apiGet<{ events: SupportedEvent[] }>('/notification_test_render/events/').then(
    (r) => r.events,
  );
}

// Re-export so the page has one import surface for the context-source controls.
export type { EvaluateContext, OverrideChannel, SupportedEvent };
